import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { useCapture } from './Capture';
import { AiExplanation, UnitField } from './CaptureSheets';
import { clockTime, kcal } from '../lib/format';
import { today } from '../lib/dates';
import { useApp } from '../lib/AppContext';
import { useAiCredits } from '../lib/credits';

const FIELD = 'input h-11 rounded-[14px] border-line text-base';
const PILL = 'btn h-11 min-h-11 rounded-full px-4 text-sm font-semibold';

// Edits a logged workout. `mode`: "kcal" focuses the kcal to type them ("Nhập kcal"); "ai" asks the AI for them
// straight away ("AI ước tính · 1 lượt"), to review and save like a typed number.
function EditRow({ activity, onAction, onDone, mode }) {
  const { api, membership } = useApp();
  const credits = useAiCredits(1);
  const kcalRef = useRef(null);
  const durationRef = useRef(null);
  const started = useRef(false);
  const [values, setValues] = useState({
    name: activity.name, duration: activity.duration_minutes ? String(activity.duration_minutes) : '', calories: activity.calories ? String(activity.calories) : '',
  });
  // The AI's answer while its number is unchanged.
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const set = (key) => (value) => {
    setError(null);
    setValues((v) => ({ ...v, [key]: value }));
    if (key === 'calories') setAi((a) => (a && String(a.calories) === String(value) ? a : null));
  };
  const run = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const estimate = () => {
    if (credits.blocked(estimate)) return;
    if (!(Number(values.duration) > 0)) {
      setError('Hãy nhập thời lượng để AI ước tính kcal.');
      durationRef.current?.focus();
      return;
    }
    run('analyze', async () => {
      const result = await api.post('/activities/scan', {
        sessions: [{ name: values.name.trim(), duration_minutes: Number(values.duration), notes: activity.notes || '' }],
      }, { idempotencyKey: credits.key() });
      const found = result.activities[0];
      if (!found) throw new Error(result.note || 'AI không ước tính được buổi tập này. Hãy nhập kcal tay.');
      credits.used();
      setValues((v) => ({ ...v, calories: String(found.calories) }));
      setAi(found);
    });
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (mode === 'kcal') kcalRef.current?.focus();
    if (mode === 'ai') estimate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => run('save', async () => {
    await onAction('patch', `/activities/${activity.id}`, {
      name: values.name, duration_minutes: values.duration, calories: values.calories, ai_estimated: !!ai,
    });
    onDone();
  });

  const credit = membership?.credits;
  const leftText = credit && credit.left != null ? `${credit.left}/${credit.limit}` : null;
  return (
    <form className="flex flex-col gap-3 py-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <input required className={`${FIELD} w-full`} aria-label="Tên buổi tập" value={values.name} onChange={(e) => set('name')(e.target.value)} />
      <div className="flex gap-2">
        <UnitField label="Thời lượng" unit="phút" placeholder="45" value={values.duration} onChange={set('duration')} inputRef={durationRef} />
        <UnitField label="Kcal đã đốt" unit="kcal" placeholder="Chưa có" value={values.calories} onChange={set('calories')}
          approx={!!ai} highlight={!!ai} inputRef={kcalRef} />
      </div>
      {busy === 'analyze' && <p className="flex items-center gap-2 text-sm text-muted"><span className="loading loading-spinner loading-sm" /> AI đang ước tính…</p>}
      {ai && (
        <>
          <p className="flex gap-2 rounded-[14px] bg-[#e3f1e9] p-3 text-sm text-[#15261f]">
            <Icon name="sparkles" className="mt-0.5 size-4 shrink-0 text-[#17804f]" />
            <span><b>Đã dùng 1 lượt{leftText ? ` · còn ${leftText} hôm nay` : ''}.</b> Sửa số kcal nếu đồng hồ báo khác, rồi bấm Lưu.</span>
          </p>
          <AiExplanation text={ai.explanation} />
        </>
      )}
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary h-11 flex-1 rounded-[14px] border-0" disabled={!!busy}>
          {values.calories && Number(values.calories) > 0 ? `Lưu · +${kcal(Number(values.calories))} kcal` : 'Lưu'}
        </button>
        <button type="button" className="btn btn-ghost h-11 rounded-[14px]" onClick={onDone}>Hủy</button>
        <button type="button" className="btn btn-circle btn-ghost size-11 text-error" aria-label={`Xóa ${activity.name}`} disabled={!!busy}
          onClick={() => window.confirm(`Xóa ${activity.name}?`) && run('delete', async () => { await onAction('del', `/activities/${activity.id}`); onDone(); })}>
          <Icon name="trash" className="size-5" />
        </button>
      </div>
      {!ai && !(Number(values.calories) > 0) && busy !== 'analyze' && (
        <button type="button" className="min-h-11 text-sm font-semibold text-brand-text" disabled={!!busy} onClick={estimate}>
          <Icon name="sparkles" className="inline size-4" /> Để AI ước tính kcal · 1 lượt
        </button>
      )}
    </form>
  );
}

// One workout: name, duration · when it was logged, +kcal on the right. Without kcal: "Chưa có kcal" and the two ways to add them.
function ActivityRow({ activity: a, onEdit }) {
  const meta = [a.duration_minutes && `${a.duration_minutes} phút`, a.logged_at && clockTime(a.logged_at).replace(/^0(\d)/, '$1'), a.calories && a.ai_estimated && 'AI ước tính'].filter(Boolean).join(' · ');
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold">{a.name}</span>
        <span className="block text-sm text-muted">
          {meta}
          {!a.calories && <span className="font-semibold text-warning">{meta ? ' · ' : ''}Chưa có kcal</span>}
        </span>
      </span>
      {a.calories ? <span className="tabular shrink-0 text-[15px] font-bold text-brand-text">+{kcal(a.calories)} kcal</span> : null}
    </>
  );
  if (!onEdit) return <div className="flex items-center gap-3 py-3">{body}</div>;
  return (
    <div className="py-3">
      <button type="button" className="flex w-full items-center gap-3 text-left" onClick={() => onEdit('edit')}
        aria-label={`${a.name}, ${a.calories ? `${kcal(a.calories)} kcal` : 'chưa có kcal'}. Chạm để sửa`}>
        {body}
      </button>
      {!a.calories && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className={`${PILL} border-line bg-base-100`} onClick={() => onEdit('kcal')}>Nhập kcal</button>
          <button type="button" className={`${PILL} border-[#17804f] bg-base-100 text-[#17804f]`} onClick={() => onEdit('ai')}>
            <Icon name="sparkles" className="size-4" /> AI ước tính · 1 lượt
          </button>
        </div>
      )}
    </div>
  );
}

// One way in: "+ Ghi buổi tập" opens the workout sheet. Logged: one row per workout, tap to edit; one without kcal
// still counts as a workout day (adds 0) and offers "Nhập kcal" or the AI's estimate right here.
export default function ActivityCard({ day, onAction }) {
  const { editable } = useApp();
  const capture = useCapture();
  // { id, mode } of the workout being edited.
  const [editing, setEditing] = useState(null);
  const empty = day.activities.length === 0;
  const burned = day.activities.reduce((sum, a) => sum + (a.calories || 0), 0);

  return (
    <section className="rounded-[20px] bg-base-100 p-5">
      <div className="mb-1 flex items-baseline justify-between">
        <h2 className="text-lg font-bold">Vận động</h2>
        <span className={`tabular text-sm font-semibold ${burned ? 'text-train' : 'text-muted'}`}>+{kcal(burned)} kcal</span>
      </div>

      {empty && <p className="mb-3 text-sm text-muted">{day.date === today() ? 'Chưa ghi buổi tập nào hôm nay.' : 'Chưa ghi buổi tập nào.'}</p>}

      {!empty && (
        <ul className="divide-y divide-line">
          {day.activities.map((a) => (
            <li key={a.id}>
              {editing?.id === a.id
                ? <EditRow activity={a} mode={editing.mode} onAction={onAction} onDone={() => setEditing(null)} />
                : <ActivityRow activity={a} onEdit={editable ? (mode) => setEditing({ id: a.id, mode }) : null} />}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <button type="button" onClick={() => capture.open('workout', { date: day.date })}
          className="btn mt-2 h-12 w-full rounded-[14px] border border-dashed border-[#17804f]/60 bg-[#e3f1e9]/40 text-[15px] font-semibold text-brand-text">
          <Icon name="plus" className="size-4" /> Ghi buổi tập
        </button>
      )}
    </section>
  );
}
